// src/components/CharacterList.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Users, Tent, FlaskConical } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import AppHeader from './layout/AppHeader';
import { cn } from '../lib/utils';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { trackCharacterDeleted } from '../services/analyticsService';
import HomebrewWorkshop from './homebrew/HomebrewWorkshop';
import WelcomeModal from './WelcomeModal';

// Direct Firestore imports
import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  updateDoc,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase/config';

const TABS = [
  { id: 'characters', label: 'Characters', icon: Users },
  { id: 'campaigns', label: 'Campaigns', icon: Tent },
  { id: 'homebrew', label: 'Homebrew', icon: FlaskConical },
];

const CharacterList = ({ onSelectCharacter, onCreateNew }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [characters, setCharacters] = useState([]);
  const [gmCampaigns, setGmCampaigns] = useState([]);
  const [playerCampaigns, setPlayerCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const validTabs = TABS.map(t => t.id);
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(
    validTabs.includes(tabParam) ? tabParam : 'characters'
  );

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    if (tab === 'characters') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const parseCampaignId = (input) => {
    const trimmed = input.trim();
    const joinMatch = trimmed.match(/\/join-campaign\/([A-Za-z0-9]+)/);
    if (joinMatch) return joinMatch[1];
    const campaignMatch = trimmed.match(/\/campaign\/([A-Za-z0-9]+)/);
    if (campaignMatch) return campaignMatch[1];
    if (/^[A-Za-z0-9]{10,}$/.test(trimmed)) return trimmed;
    return null;
  };

  const handleJoinCampaign = () => {
    const id = parseCampaignId(joinInput);
    if (id) {
      navigate(`/join-campaign/${id}`);
    } else {
      toast.error('Could not parse a campaign ID. Paste an invite link or campaign ID.');
    }
  };

  // Responsive breakpoint detection
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    if (!loading && characters.length === 0 && gmCampaigns.length === 0 && playerCampaigns.length === 0 && !localStorage.getItem('chag_welcomed')) {
      setShowWelcome(true);
    }
  }, [loading, characters, gmCampaigns, playerCampaigns]);

  // Add a refresh when component becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      await Promise.all([
        loadCharacters(),
        loadGMCampaigns(),
        loadPlayerCampaigns()
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadCharacters = async () => {
    try {
      if (user) {
        // Get user's characters directly from Firestore
        const charactersRef = collection(db, 'characters');
        const q = query(
          charactersRef,
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);

        const userCharacters = [];
        querySnapshot.forEach((doc) => {
          userCharacters.push({
            id: doc.id,
            ...doc.data()
          });
        });

        // Sort on client side to avoid needing composite index
        userCharacters.sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || 0;
          const bTime = b.updatedAt?.toMillis?.() || 0;
          return bTime - aTime; // desc order
        });

        setCharacters(userCharacters);
      }
    } catch (err) {
      console.error('Error loading characters:', err);
    }
  };

  const loadGMCampaigns = async () => {
    try {
      if (user) {
        const campaignsRef = collection(db, 'campaigns');
        const q = query(
          campaignsRef,
          where('gmId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);

        const campaigns = [];

        for (const docSnapshot of querySnapshot.docs) {
          const campaignData = { id: docSnapshot.id, ...docSnapshot.data() };

          // Enrich player data with character names
          if (campaignData.players && campaignData.players.length > 0) {
            const enhancedPlayers = [];
            for (const player of campaignData.players) {
              try {
                const characterDoc = await getDoc(doc(db, 'characters', player.characterId));
                const characterName = characterDoc.exists() ? characterDoc.data().name : 'Unknown Character';
                enhancedPlayers.push({
                  ...player,
                  characterName
                });
              } catch (error) {
                console.error('Error loading character name:', error);
                enhancedPlayers.push({
                  ...player,
                  characterName: 'Unknown Character'
                });
              }
            }
            campaignData.players = enhancedPlayers;
          }

          campaigns.push(campaignData);
        }

        // Sort on client side to avoid needing composite index
        campaigns.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime; // desc order
        });

        setGmCampaigns(campaigns);
      }
    } catch (err) {
      console.error('Error loading GM campaigns:', err);
    }
  };

  const loadPlayerCampaigns = async () => {
    try {
      if (user) {
        // Get campaigns where user is a player
        const campaignsRef = collection(db, 'campaigns');
        const querySnapshot = await getDocs(campaignsRef);

        const campaigns = [];

        for (const docSnapshot of querySnapshot.docs) {
          const campaignData = docSnapshot.data();
          // Check if user is in the players array
          const isPlayer = campaignData.players?.some(player => player.userId === user.uid);

          if (isPlayer) {
            const enrichedCampaign = { id: docSnapshot.id, ...campaignData };

            // Get GM name if it's not the current user
            if (campaignData.gmId && campaignData.gmId !== user.uid) {
              // Use stored GM name from campaign data, or try to get it from players array if GM ever joined as player
              if (campaignData.gmName && campaignData.gmName !== 'Game Master') {
                enrichedCampaign.gmName = campaignData.gmName;
              } else {
                // Fallback: check if GM appears in players array of any campaign (they might have joined their own campaign)
                const allUsersInCampaign = [...(campaignData.players || [])];
                const gmAsPlayer = allUsersInCampaign.find(player => player.userId === campaignData.gmId);

                if (gmAsPlayer && gmAsPlayer.playerName) {
                  enrichedCampaign.gmName = gmAsPlayer.playerName;
                } else {
                  // Final fallback: try to extract name from email if available, or use generic
                  enrichedCampaign.gmName = 'Game Master';
                }
              }
            }

            campaigns.push(enrichedCampaign);
          }
        }

        // Sort on client side
        campaigns.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime; // desc order
        });

        setPlayerCampaigns(campaigns);
      }
    } catch (err) {
      console.error('Error loading player campaigns:', err);
    }
  };

  const handleDeleteCharacter = async (characterId, characterName) => {
    if (window.confirm(`Are you sure you want to delete ${characterName}? This action cannot be undone.`)) {
      try {
        // Get character data for analytics before deleting
        const characterToDelete = characters.find(char => char.id === characterId);

        // Delete character directly from Firestore
        const characterRef = doc(db, 'characters', characterId);
        await deleteDoc(characterRef);
        setCharacters(characters.filter(char => char.id !== characterId));

        // Track deletion in analytics
        if (characterToDelete) {
          trackCharacterDeleted(characterToDelete);
        }
      } catch (error) {
        setError('Failed to delete character');
        console.error('Error deleting character:', error);
      }
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the campaign "${campaign.name}"? This action cannot be undone and will remove the campaign for all players.`
    );

    if (confirmed) {
      const doubleConfirm = window.confirm(
        'This will permanently delete the campaign and cannot be undone. Are you absolutely sure?'
      );

      if (doubleConfirm) {
        try {
          const campaignRef = doc(db, 'campaigns', campaign.id);
          await deleteDoc(campaignRef);

          toast.success('Campaign deleted successfully.');
          loadData(); // Refresh the data
        } catch (error) {
          console.error('Error deleting campaign:', error);
          toast.error('Failed to delete campaign. Please try again.');
        }
      }
    }
  };

  const handleLeaveCampaign = async (campaign) => {
    const confirmed = window.confirm(
      'Are you sure you want to leave this campaign? This action cannot be undone.'
    );

    if (confirmed) {
      try {
        // Find the player's entry to remove
        const playerToRemove = campaign.players?.find(player => player.userId === user.uid);
        if (playerToRemove) {
          const campaignRef = doc(db, 'campaigns', campaign.id);
          await updateDoc(campaignRef, {
            players: arrayRemove(playerToRemove)
          });

          toast.success('You have left the campaign successfully.');
          loadData(); // Refresh the data
        }
      } catch (error) {
        console.error('Error leaving campaign:', error);
        toast.error('Failed to leave campaign. Please try again.');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading your characters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen text-gray-100">
      {showWelcome && (
        <WelcomeModal onClose={() => setShowWelcome(false)} />
      )}
      {/* Branded Header */}
      <AppHeader />

      {/* Desktop Sidebar Navigation */}
      {isDesktop && (
        <aside
          className="hidden lg:flex flex-col w-sidebar bg-surface-1 fixed left-0 top-[73px] bottom-0 z-40 shadow-2xl"
          role="navigation"
          aria-label="Home screen sections"
        >
          {/* User Identity Card */}
          <div className="px-4 py-4 space-y-2">
            <h2 className="text-heading-lg text-red-400 font-bold truncate font-serif">
              Welcome
            </h2>
            <div className="text-sm space-y-1 text-gray-300">
              <div className="font-medium">
                <span className="text-gray-100">{user?.displayName || 'Player'}</span>
              </div>
              <div className="text-gray-400 text-xs">
                {characters.length} Character{characters.length !== 1 ? 's' : ''} • {gmCampaigns.length + playerCampaigns.length} Campaign{(gmCampaigns.length + playerCampaigns.length) !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 py-2 px-3" role="tablist">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`${tab.id}-panel`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'w-full flex items-center gap-4 px-4 py-4 mb-1',
                    'text-left transition-all duration-200',
                    'relative rounded-lg',
                    'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset',
                    isActive
                      ? 'text-red-400 bg-red-900/20 border border-red-700/50'
                      : 'text-gray-400 hover:text-red-300 hover:bg-red-900/10'
                  )}
                >
                  {isActive && (
                    <div
                      className="absolute left-0 top-1 bottom-1 w-1 bg-red-700 rounded-r-full shadow-lg shadow-red-700/50"
                      aria-hidden="true"
                    />
                  )}
                  <Icon className="w-7 h-7 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="text-nav font-medium">{tab.label}</span>
                  </div>
                </button>
              );
            })}
          </nav>

          {/* Legal Links */}
          <div className="mt-auto px-4 py-3 border-t border-gray-700/50">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
              <Link to="/about" className="hover:text-gray-300 transition-colors">About</Link>
              <span>·</span>
              <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms</Link>
            </div>
            <p className="text-[10px] text-gray-600 mt-1">
              Compatible with Daggerheart™ under DPCGL
            </p>
          </div>
        </aside>
      )}

      {/* Mobile Bottom Tab Navigation */}
      {!isDesktop && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-50 bg-surface-1 border-t-2 border-red-700 shadow-2xl safe-bottom lg:hidden"
          role="tablist"
          aria-label="Home screen sections"
        >
          <div className="flex justify-around items-stretch min-h-[56px]">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`${tab.id}-panel`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 flex flex-col items-center justify-center gap-1',
                    'min-h-[48px] px-3 py-2',
                    'transition-all duration-200',
                    'relative',
                    'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset',
                    isActive
                      ? 'text-red-400'
                      : 'text-gray-400 hover:text-red-300'
                  )}
                >
                  <Icon
                    className={cn(
                      'w-6 h-6 transition-transform duration-200',
                      isActive && 'scale-110'
                    )}
                  />
                  <span className="text-tiny font-medium">
                    {tab.label}
                  </span>
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-1 bg-red-700 rounded-t-full shadow-lg shadow-red-700/50"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* Main Content Area */}
      <main className={cn(
        'pt-[73px]',
        'pb-20 lg:pb-0',
        'lg:ml-sidebar',
        'min-w-0 max-w-full flex-1',
        'lg:rounded-tl-2xl'
      )}>
        <div className="max-w-7xl mx-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/50 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded">
              {error}
            </div>
          )}

          {/* CHARACTERS TAB */}
          {activeTab === 'characters' && (
            <div
              role="tabpanel"
              id="characters-panel"
              aria-labelledby="characters-tab"
            >
              {/* Create Character Button */}
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-3xl font-bold text-red-400 font-serif">
                  Characters
                </h2>
                <button
                  onClick={onCreateNew}
                  className="px-6 py-3 bg-gray-800 text-gray-100 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200 font-medium"
                >
                  + Create Character
                </button>
              </div>

              {/* Characters Grid */}
              {characters.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    No characters yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Create your first character to get started on your Daggerheart adventure!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {characters.map((character) => {
                    // Find campaign info for this character
                    const characterCampaign = [...gmCampaigns, ...playerCampaigns].find(
                      campaign => campaign.players?.some(player => player.characterId === character.id)
                    );

                    return (
                      <div
                        key={character.id}
                        onClick={() => onSelectCharacter(character)}
                        className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-6 hover:border-red-600 transition-all cursor-pointer group"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-red-400 font-serif group-hover:text-red-300 transition-colors">
                              {character.name || 'Unnamed Character'}
                            </h3>
                            <p className="text-gray-300">
                              Level {character.level || 1} {character.class || 'Unknown Class'}
                            </p>
                            {characterCampaign && (
                              <p className="text-sm text-red-300 font-medium">
                                In Campaign: {characterCampaign.name}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCharacter(character.id, character.name);
                            }}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                            title="Delete character"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>

                        <div className="space-y-2">
                          {character.ancestry && (
                            <p className="text-sm text-gray-300">
                              <span className="font-medium text-gray-400">Ancestry:</span> {Array.isArray(character.ancestry) ? character.ancestry.join(', ') : character.ancestry}
                            </p>
                          )}
                          {character.community && (
                            <p className="text-sm text-gray-300">
                              <span className="font-medium text-gray-400">Community:</span> {character.community}
                            </p>
                          )}
                          <p className="text-sm text-gray-300">
                            <span className="font-medium text-gray-400">Last Updated:</span> {formatDate(character.updatedAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* CAMPAIGNS TAB */}
          {activeTab === 'campaigns' && (
            <div
              role="tabpanel"
              id="campaigns-panel"
              aria-labelledby="campaigns-tab"
            >
              {/* Create Campaign Button */}
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-3xl font-bold text-red-400 font-serif">
                  Campaigns
                </h2>
                <button
                  onClick={() => navigate('/create-campaign')}
                  className="px-6 py-3 bg-gray-800 text-gray-100 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200 font-medium"
                >
                  + Create Campaign
                </button>
              </div>

              {/* Join a Campaign */}
              <div className="mb-6 border border-dashed border-gray-600 rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">Have an invite link or campaign ID?</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={joinInput}
                    onChange={(e) => setJoinInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleJoinCampaign()}
                    placeholder="Paste invite link or campaign ID"
                    className="flex-1 px-3 py-2 bg-[#232323] border border-gray-600 rounded-md text-gray-100 text-sm placeholder-gray-500"
                  />
                  <button
                    onClick={handleJoinCampaign}
                    className="px-4 py-2 bg-red-900/40 text-red-300 rounded-md hover:bg-red-900/60 border border-red-700 transition-all duration-200 text-sm font-medium"
                  >
                    Join
                  </button>
                </div>
              </div>

              {/* GM Campaigns Section */}
              {gmCampaigns.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-purple-400 mb-4">
                    My Campaigns (GM)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gmCampaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        onClick={() => navigate(`/campaign/${campaign.id}`)}
                        className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-6 hover:border-red-600 transition-all border-l-4 border-l-purple-500 relative cursor-pointer group"
                      >
                        {/* Delete Campaign Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCampaign(campaign);
                          }}
                          className="absolute top-3 right-3 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                          title="Delete Campaign"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>

                        <div className="mb-4 pr-8">
                          <h3 className="text-xl font-bold text-red-400 font-serif group-hover:text-red-300 transition-colors">
                            {campaign.name}
                          </h3>
                          <p className="text-gray-300">
                            Frame: {campaign.campaignFrame || 'core-rules'}
                          </p>
                          <p className="text-sm text-purple-400 font-medium">
                            GM • {campaign.players?.length || 0} players
                          </p>
                        </div>

                        {campaign.players && campaign.players.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-400 mb-2">
                              Party Members:
                            </h4>
                            <div className="text-sm text-gray-300">
                              {campaign.players.map((player, index) => (
                                <div key={index}>• {player.characterName || `Player ${index + 1}`}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const shareableLink = `${window.location.origin}/join-campaign/${campaign.id}`;
                              navigator.clipboard.writeText(shareableLink).then(() => {
                                toast.success('Campaign invite link copied to clipboard!');
                              }).catch(() => {
                                prompt('Copy this link to invite players:', shareableLink);
                              });
                            }}
                            className="w-full px-4 py-2 border border-red-700 text-red-400 text-sm rounded-md hover:bg-red-900/20 transition-colors font-medium"
                          >
                            Copy Invite Link
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Player Campaigns Section */}
              {playerCampaigns.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-green-400 mb-4">
                    Joined Campaigns (Player)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {playerCampaigns.map((campaign) => {
                      // Find the user's character in this campaign
                      const userPlayer = campaign.players?.find(player => player.userId === user.uid);
                      const userCharacter = characters.find(char => char.id === userPlayer?.characterId);

                      return (
                        <div
                          key={campaign.id}
                          onClick={() => navigate(`/campaign/${campaign.id}?view=player`)}
                          className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-6 hover:border-red-600 transition-all border-l-4 border-l-green-500 relative cursor-pointer group"
                        >
                          {/* Leave Campaign Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLeaveCampaign(campaign);
                            }}
                            className="absolute top-3 right-3 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-full transition-colors"
                            title="Leave Campaign"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>

                          <div className="mb-4 pr-8">
                            <h3 className="text-xl font-bold text-red-400 font-serif group-hover:text-red-300 transition-colors">
                              {campaign.name}
                            </h3>
                            <p className="text-gray-300">
                              Frame: {campaign.campaignFrame || 'core-rules'}
                            </p>
                            <p className="text-sm text-green-400 font-medium">
                              Player • {campaign.players?.length || 0} total players
                            </p>
                          </div>

                          {userCharacter && (
                            <div className="mb-4 p-3 bg-green-900/30 border border-green-700 rounded">
                              <h4 className="text-sm font-medium text-gray-400 mb-1">
                                Your Character:
                              </h4>
                              <div className="text-sm text-gray-300">
                                <strong>{userCharacter.name}</strong>
                                <br />
                                Level {userCharacter.level} {userCharacter.class}
                              </div>
                            </div>
                          )}

                          <div className="text-sm text-gray-300">
                            <p>GM: {campaign.gmId === user.uid ? 'You' : (campaign.gmName || 'Other GM')}</p>
                            <p>Party Size: {campaign.players?.length || 0}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Coming Soon Teaser */}
              {(gmCampaigns.length > 0 || playerCampaigns.length > 0) && (
                <div className="mt-8">
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
                    <p className="text-xl font-medium text-gray-500 mb-2">More coming soon</p>
                    <p className="text-sm text-gray-500">
                      Additional campaign features in development
                    </p>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {gmCampaigns.length === 0 && playerCampaigns.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-400 mb-4">No campaigns yet</p>
                  <p className="text-gray-500">Create a campaign to start your adventure!</p>
                </div>
              )}
            </div>
          )}

          {/* HOMEBREW TAB — always mounted to preserve Brewmaster conversation state */}
          <div role="tabpanel" id="homebrew-panel" aria-labelledby="homebrew-tab" className={activeTab === 'homebrew' ? '' : 'hidden'}>
            <HomebrewWorkshop />
          </div>
        </div>
      </main>
    </div>
  );
};

export default CharacterList;
